import type { BreadcrumbEntry } from './types.js';

export class BreadcrumbBuffer {
  private readonly buffer: BreadcrumbEntry[] = [];
  private readonly max: number;

  constructor(max = 100) {
    this.max = max;
  }

  add(crumb: BreadcrumbEntry): void {
    if (this.buffer.length >= this.max) {
      this.buffer.shift();
    }
    this.buffer.push(crumb);
  }

  getAll(): BreadcrumbEntry[] {
    return [...this.buffer];
  }

  clear(): void {
    this.buffer.length = 0;
  }
}

// ─── Auto-capture helpers ─────────────────────────────────────────────────────

type AddFn = (crumb: BreadcrumbEntry) => void;

/**
 * Patch History API (pushState / replaceState / popstate) to capture
 * navigation breadcrumbs.
 */
export function installNavigationBreadcrumbs(add: AddFn): () => void {
  if (typeof window === 'undefined') return () => undefined;

  const originalPush = history.pushState.bind(history);
  const originalReplace = history.replaceState.bind(history);

  history.pushState = function (...args) {
    add({
      timestamp: new Date().toISOString(),
      type: 'navigation',
      category: 'navigation',
      message: `Navigated to ${String(args[2] ?? '')}`,
      data: { to: String(args[2] ?? '') },
    });
    return originalPush(...args);
  };

  history.replaceState = function (...args) {
    add({
      timestamp: new Date().toISOString(),
      type: 'navigation',
      category: 'navigation',
      message: `Replaced state: ${String(args[2] ?? '')}`,
      data: { to: String(args[2] ?? '') },
    });
    return originalReplace(...args);
  };

  const onPopState = () => {
    add({
      timestamp: new Date().toISOString(),
      type: 'navigation',
      category: 'navigation',
      message: `Navigated back/forward to ${window.location.href}`,
      data: { to: window.location.href },
    });
  };

  window.addEventListener('popstate', onPopState);

  return () => {
    history.pushState = originalPush;
    history.replaceState = originalReplace;
    window.removeEventListener('popstate', onPopState);
  };
}

/**
 * Capture click events as breadcrumbs (element tag + text).
 */
export function installClickBreadcrumbs(add: AddFn): () => void {
  if (typeof document === 'undefined') return () => undefined;

  const handler = (e: MouseEvent) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const tag = target.tagName?.toLowerCase() ?? 'unknown';
    const text = (target.textContent ?? '').trim().slice(0, 50);
    const id = target.id ? `#${target.id}` : '';
    const cls = target.className && typeof target.className === 'string'
      ? `.${target.className.split(' ').join('.')}`
      : '';
    add({
      timestamp: new Date().toISOString(),
      type: 'user',
      category: 'ui.click',
      message: `Click on <${tag}${id}${cls}>`,
      data: { tag, text: text || undefined },
    });
  };

  document.addEventListener('click', handler, { capture: true, passive: true });

  return () => {
    document.removeEventListener('click', handler, { capture: true });
  };
}

/**
 * Capture console.warn and console.error calls as breadcrumbs.
 */
export function installConsoleBreadcrumbs(add: AddFn): () => void {
  if (typeof console === 'undefined') return () => undefined;

  const originalWarn = console.warn.bind(console);
  const originalError = console.error.bind(console);

  const formatArgs = (args: unknown[]): string =>
    args.map((a) => (typeof a === 'string' ? a : JSON.stringify(a))).join(' ').slice(0, 200);

  console.warn = (...args: unknown[]) => {
    add({
      timestamp: new Date().toISOString(),
      type: 'console',
      category: 'console',
      message: formatArgs(args),
      data: { level: 'warning' },
    });
    originalWarn(...args);
  };

  console.error = (...args: unknown[]) => {
    add({
      timestamp: new Date().toISOString(),
      type: 'console',
      category: 'console',
      message: formatArgs(args),
      data: { level: 'error' },
    });
    originalError(...args);
  };

  return () => {
    console.warn = originalWarn;
    console.error = originalError;
  };
}
